import {
  ObservableQuery,
  OperationVariables,
  QueryHookOptions,
  useQuery,
} from "@apollo/client/react";
import { Loading } from "common/components/Loading";
import { SimpleErrorMessage } from "common/components/SimpleErrorMessage";
import { DocumentNode } from "graphql";
import React, { ReactNode } from "react";
import { parseErrorFromGraphql } from "./utils";

export interface SimpleQueryResponse<
  TData,
  TVariables extends OperationVariables,
> {
  data?: TData;
  loading?: ReactNode;
  loadingOrError?: ReactNode;
  refetch: ObservableQuery<TData, TVariables>["refetch"];
}

export function useSimpleQuery<
  TData,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode,
  options?: {
    variables: QueryHookOptions<TData, TVariables>["variables"];
  },
): SimpleQueryResponse<TData, TVariables> {
  const { loading, error, data, refetch } = useQuery<TData, TVariables>(query, {
    variables: options?.variables,
  });

  if (loading) {
    const loadingComp = <Loading />;

    return {
      data,
      loading: loadingComp,
      // If we have data, we perfer to render it so that this
      // is set to undefined. If needed, the loading value can
      // be checked directly.
      loadingOrError: data ? undefined : <Loading />,
      refetch,
    };
  }

  if (error) {
    return {
      data,
      loadingOrError: (
        <SimpleErrorMessage>{parseErrorFromGraphql(error)}</SimpleErrorMessage>
      ),
      refetch,
    };
  }

  return {
    data,
    refetch,
  };
}
